/**
 * Combien de temps il faut pour obtenir un objet, en secondes de jeu.
 *
 * C'est la mesure qui manquait au marché. `prixVente` ne dépend que du **niveau**
 * de l'objet (§7) — parfait pour un rachat, où l'on se débarrasse de son butin,
 * catastrophique pour une vente : le Galet cramoisi, qui tombe à 6 % d'un boss
 * de donjon derrière une clé qu'il faut fabriquer, se retrouvait au même prix
 * que n'importe quelle babiole de niveau 50. Le comptoir le cédait à 444 kamas,
 * soit trois mille par heure de jeu.
 *
 * Dans un incrémental, la vraie monnaie est le **temps**. On calcule donc, pour
 * chaque objet, l'espérance de secondes qu'il coûte à qui va le chercher lui-
 * même ; le comptoir facture ce temps-là, majoré. Acheter reste toujours une
 * moins bonne affaire que farmer — mais on récupère sa soirée.
 */
import type { Contenu, Item } from "./contenu.ts"
import { indexer } from "./provenance.ts"
import { tempsRecolte } from "./recolte.ts"

/**
 * Le temps d'une victoire, au niveau où l'on affronte la famille pour de bon.
 *
 * On ne simule pas un combat réel : le prix ne peut pas dépendre du personnage
 * qui regarde l'étal, sinon deux joueurs verraient deux prix. C'est la durée
 * d'un joueur correctement équipé pour la zone, soit le plancher du moteur.
 */
const TEMPS_KILL_S = 2

/** Une descente complète : cinq salles de gardes, puis le boss. */
const TEMPS_DONJON_S = 180

/**
 * Ce qu'on ne sait pas chercher.
 *
 * Un archimonstre apparaît au hasard, une recherche demande de tomber sur un
 * indice : ni l'un ni l'autre ne se planifie. Plutôt que d'inventer une
 * espérance, on retient une valeur haute — ce sont des objets qu'on ne devrait
 * pas pouvoir acheter à la légère.
 */
const TEMPS_RARE_S = 3 * 3600

/** Au-delà, la recette est trop profonde pour qu'on la déplie encore. */
const PROFONDEUR_MAX = 3

let cache: { c: Contenu; temps: Map<number, number> } | null = null

/**
 * Secondes attendues pour obtenir **une** unité de chaque objet.
 *
 * Mémoïsé par référence de contenu : le calcul parcourt tout le bestiaire et
 * toutes les recettes, et l'étal le demande à chaque rendu.
 */
export function tempsParObjet(c: Contenu): Map<number, number> {
  if (cache?.c === c) return cache.temps
  const temps = new Map<number, number>()
  const index = indexer(c)

  // 1. Ce qui se ramasse ou se tue : une espérance directe.
  for (const [id, o] of index.par) {
    if (o.type === "recolte") {
      const r = Object.values(c.recoltes).find((x) => x.item === id)
      // Un métier au niveau requis, pas au niveau 1 : c'est l'état normal de
      // qui récolte cette ressource-là.
      temps.set(id, r ? tempsRecolte(r, r.niveauRequis) : 5)
    } else if (o.type === "drop") {
      const taux = Math.max(0.01, o.familles[0]?.taux ?? 0.01)
      temps.set(id, TEMPS_KILL_S * (100 / taux))
    } else if (o.type === "donjon") {
      // Le boss triple le taux affiché, et il faut refaire toute la descente —
      // clé comprise, mais celle-ci se fabrique et compte donc à part.
      const taux = Math.max(0.01, (o.donjons[0]?.taux ?? 0.01) * 3)
      temps.set(id, TEMPS_DONJON_S * (100 / taux))
    } else if (o.type === "archimonstre" || o.type === "recherche") {
      temps.set(id, TEMPS_RARE_S)
    }
  }

  // 2. Ce qui se fabrique : la somme de ses ingrédients, dépliée.
  const recettes = new Map(c.recettes.map((r) => [r.resultat, r]))
  const enCours = new Set<number>()
  const tempsDe = (id: number, profondeur: number): number => {
    const connu = temps.get(id)
    if (connu !== undefined) return connu
    const r = recettes.get(id)
    // Une recette circulaire ou trop profonde : on s'arrête sur une valeur
    // haute plutôt que de boucler.
    if (!r || profondeur > PROFONDEUR_MAX || enCours.has(id)) return TEMPS_RARE_S
    enCours.add(id)
    const total = r.ingredients.reduce(
      (a, i) => a + tempsDe(i.item, profondeur + 1) * Math.max(1, i.qte), 0)
    enCours.delete(id)
    temps.set(id, total)
    return total
  }
  for (const r of c.recettes) tempsDe(r.resultat, 0)

  cache = { c, temps }
  return temps
}

/** Secondes attendues pour un exemplaire, ou `null` si l'objet ne s'obtient pas. */
export function tempsPourUn(c: Contenu, it: Item): number | null {
  return tempsParObjet(c).get(it.id) ?? null
}
